import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDataSource } from '../contexts/DataSourceContext';
import { useStore } from '../contexts/StoreContext';
import {
  loadSourceReadModels,
  type SourceReadModel,
} from '../lib/source-read-model/read-model';

export function useSourceReadModels(): {
  isLoading: boolean;
  models: SourceReadModel[];
  error: string | null;
} {
  const { store, isReady } = useStore();
  const { wallet, isAuthenticated } = useAuth();
  const { dataSources } = useDataSource();
  const userId = wallet?.address ?? 'anonymous';
  const [models, setModels] = useState<SourceReadModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!isReady || !isAuthenticated || !store) {
      setModels([]);
      setIsLoading(false);
      setError(null);
      return () => { cancelled = true; };
    }

    setIsLoading(true);
    setError(null);

    loadSourceReadModels({ store, userId, dataSources })
      .then(nextModels => {
        if (cancelled) return;
        setModels(nextModels);
      })
      .catch(err => {
        if (cancelled) return;
        setModels([]);
        setError(err instanceof Error ? err.message : 'Failed to load source read model');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [dataSources, isAuthenticated, isReady, store, userId]);

  return { isLoading, models, error };
}
